/**
 * The full-screen promotion: one document at content/promotion, which every
 * app reads on launch. Saving overwrites it; clearing it deletes it, which is
 * how the feature is turned off.
 *
 * There is no history and no collection on purpose -- the app must never be
 * asked to pick a promotion out of a list, because that would cost a query
 * per launch instead of a single document read.
 */
import { deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { COLLECTIONS, PROMOTION_DOC, STORAGE_PATHS } from '@dnyaneshwari/shared';
import type { Promotion } from '@dnyaneshwari/shared';
import { db, storage } from './firebase';

export async function getPromotion(): Promise<Promotion | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.CONTENT, PROMOTION_DOC));
  return snap.exists() ? (snap.data() as Promotion) : null;
}

export async function savePromotion(p: Omit<Promotion, 'version'>): Promise<Promotion> {
  const saved: Promotion = { ...p, version: new Date().toISOString() };
  await setDoc(doc(db, COLLECTIONS.CONTENT, PROMOTION_DOC), saved);
  return saved;
}

/** Takes the promotion down everywhere on the next launch. */
export async function clearPromotion(): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.CONTENT, PROMOTION_DOC));
}

/**
 * Every device downloads this picture once, so its size is the only part of
 * the feature with a running cost. A megabyte over a few thousand installs is
 * pennies; ten megabytes is not, and a GIF reaches ten very easily.
 */
export const IMAGE_WARN_BYTES = 1_500_000;

export function uploadPromotionImage(
  promotionId: string,
  lang: 'mr' | 'en',
  file: File,
  onProgress: (pct: number) => void
): Promise<string> {
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
  const storageRef = ref(storage, `${STORAGE_PATHS.PROMOTIONS}/${promotionId}/${lang}.${ext}`);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type || undefined });
    task.on(
      'state_changed',
      (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });
}

/** Natural width/height of a chosen file, so the overlay can reserve its shape. */
export function readAspectRatio(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : undefined);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(undefined);
    };
    img.src = url;
  });
}

/**
 * A datetime-local value ("2026-10-12T09:00") means the admin's own clock.
 * Stored as a full ISO instant so a phone in another zone shows the promotion
 * at the moment intended, not at nine o'clock wherever it happens to be.
 */
export function localToIso(local: string): string {
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

export function isoToLocal(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
